import { Fragment } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { isMultiItemArray, isValidArray, tr } from 'mmfintech-commons'
import {
  extractCheckoutMethodFieldOptionLabel,
  isCheckoutMethodFieldReadOnly,
  prepareCheckoutMethodFieldOptions
} from 'mmfintech-checkout-commons'

import BackButton from '@components/BackButton'

import { Loader } from '@components'
import { CustomInput } from '@components/CustomInput'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { VoucherTitle } from './Blixcard.styled'

import type { CheckoutMethodField, CountryChoice, UseAdditionalFieldsOutput } from 'mmfintech-checkout-commons/types'

type BlixcardRedeemStepProps = {
  paymentMethod: string
  countryCode: string
  setCountryCode: (code: string) => void
  fieldValues: UseAdditionalFieldsOutput
  hideBack: boolean
  handleBack: () => void
  handleNext: () => void
}

export const BlixcardRedeemStep = (props: BlixcardRedeemStepProps) => {
  const { paymentMethod, countryCode, setCountryCode, fieldValues, hideBack, handleBack, handleNext } = props

  const { checkoutCountries, checkoutPayError, checkoutPayFetching, session, sessionError } = useSelector(
    ({ checkout: { checkoutCountries, checkoutPayError, checkoutPayFetching, session, sessionError } }) => ({
      checkoutCountries,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError
    }),
    shallowEqual
  )

  const { country: sessionCountryCode } = session || {}

  return (
    <>
      {hideBack ? null : (
        <div className='mb-2'>
          <BackButton onClick={handleBack} />
        </div>
      )}

      <VoucherTitle datatype='redeem-voucher-page'>
        {tr('CHECKOUT.BLIXCARD.REDEEM.TITLE', 'Redeem Blixcard')}
      </VoucherTitle>

      <ErrorDisplay error={[sessionError, checkoutPayError]} />

      <div>
        {isMultiItemArray(checkoutCountries) && !sessionCountryCode ? (
          <CustomInput
            type='select'
            name='countryCode'
            data-test='country-select'
            label={tr('CHECKOUT.VOUCHER.REDEEM.COUNTRY', 'Select country')}
            options={checkoutCountries?.map((country: CountryChoice) => ({
              value: country.countryCode,
              label: country.name
            }))}
            value={countryCode}
            onChange={(_name: string, value: string) => setCountryCode(value)}
          />
        ) : null}

        {isValidArray(fieldValues.fields)
          ? fieldValues.fields.map((field: CheckoutMethodField) => {
              if (fieldValues.isFieldHidden(field) || field.name === 'bankChoiceId') {
                return null
              }

              if (field.type.toLowerCase() === 'select') {
                return (
                  <Fragment key={field.name}>
                    {isCheckoutMethodFieldReadOnly(field) ? (
                      <CustomInput
                        type='text'
                        data-test={`${field.name}-input`}
                        label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                        value={extractCheckoutMethodFieldOptionLabel(field, fieldValues.getValue(field.name))}
                        readOnly={true}
                        hideErrorLine
                      />
                    ) : (
                      <CustomInput
                        type='select'
                        data-test={`${field.name}-select`}
                        options={prepareCheckoutMethodFieldOptions(field)}
                        label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                        {...fieldValues.registerInput(field.name)}
                        menuPlacement='top'
                        hideErrorLine
                      />
                    )}
                    {Boolean(field.description) && (
                      <div className='input-description'>
                        {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                      </div>
                    )}
                  </Fragment>
                )
              }

              return (
                <Fragment key={field.name}>
                  <CustomInput
                    type={field.sensitive ? 'password' : 'text'}
                    data-test={`${field.name}-input`}
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    readOnly={isCheckoutMethodFieldReadOnly(field)}
                    autoComplete='off'
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            })
          : null}
      </div>

      <div className='link-container'>
        <a href='https://portal.blixcard.com'>
          {tr('CHECKOUT.VOUCHER.REDEEM.PURCHASE_HERE', 'Purchase Blixcard Here')}
        </a>
      </div>

      <div className='buttons-container mt-2'>
        <button
          type='button'
          disabled={!paymentMethod || checkoutPayFetching}
          className='continue-btn'
          onClick={handleNext}
          data-test='continue-button'
        >
          {checkoutPayFetching ? <Loader /> : tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
        </button>
      </div>
    </>
  )
}
